import { identity, pickBy } from "lodash";
import { ReactNode, useEffect, useMemo, useRef } from "react";
import { Field, Form } from "react-final-form";
import { useSelector } from "react-redux";

import {
  Button,
  Checkbox,
  FormField,
  Heading,
  Input,
  Modal,
  SpacingToken,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { Filter, IFilters } from "constants/filters";
import Role from "constants/role";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import CustomViewFilters from "routes/tickets/AddCustomViewModal/CustomViewFilters";
import { IAgent, selectLoggedInAgentPermission } from "store/entities/agents";
import { ITag } from "store/entities/tags";
import { ITeam } from "store/entities/teams";
import { getCustomViewFiltersForDisplay } from "store/views/tickets/helpers";
import { withVar } from "themes/helpers";
import { compose, maxLength, required } from "utils/validators";

import { ICustomViewFormData, initialValues } from "./form";

interface IProps {
  onClose: () => void;
  onCreate: (name: string, shared?: boolean) => void;
  filters: IFilters;
  filtersOrder: Filter[];
  agents: IAgent[];
  teams: ITeam[];
  tags: ITag[];
  languageLabels: Record<string, string>;
  route: TicketsRoutes;
}

const AddCustomViewModal = ({
  onClose,
  onCreate,
  filters,
  filtersOrder,
  agents,
  teams,
  tags,
  route,
  languageLabels,
}: IProps): ReactNode => {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const activeFiltersForDisplay = useMemo(() => {
    const activeFilters = pickBy(filters, identity);

    return getCustomViewFiltersForDisplay(activeFilters, { agents, teams, tags, languageLabels }, filtersOrder, route);
  }, [filters, agents, teams, tags, filtersOrder, route]);
  const isAdmin = useSelector(selectLoggedInAgentPermission) === Role.Owner;

  return (
    <Form
      initialValues={initialValues(isAdmin)}
      onSubmit={(values: ICustomViewFormData) => onCreate(values.name, values.shared)}
    >
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {({ handleSubmit, valid }: any) => (
        <form onSubmit={handleSubmit}>
          <Modal
            onClose={onClose}
            style={{ width: "600px" }}
            heading="Create new view"
            footer={
              <Flex>
                <Block marginRight="8px">
                  <Button kind="secondary" size="large" type="button" onClick={onClose}>
                    Cancel
                  </Button>
                </Block>
                <Button
                  data-testid="create-new-view-modal-button"
                  kind="primary"
                  size="large"
                  disabled={!valid}
                  type="submit"
                  // FIXME: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                  onClick={valid ? () => handleSubmit() : undefined}
                >
                  Create
                </Button>
              </Flex>
            }
          >
            <Block>
              <Field
                validate={compose(
                  required("Name is required"),
                  maxLength("Name is too long (max 1024 characters)", 1024)
                )}
                placeholder="Enter ticket view name"
                name="name"
                size="big"
              >
                {/* TODO: Fix this the next time the file is edited. */}
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {({ input, meta }: any) => (
                  <FormField
                    labelText="Ticket view name"
                    error={meta.error && meta.touched ? meta.error : undefined}
                    css={{ width: "100%" }}
                  >
                    <Input
                      error={meta.error && meta.touched ? meta.error : undefined}
                      {...input}
                      ref={inputRef}
                      id="custom-view-name"
                      data-1p-ignore
                    />
                  </FormField>
                )}
              </Field>
              {isAdmin && (
                <Block marginBottom={withVar(SpacingToken.Spacing5)}>
                  <Field
                    name="shared"
                    // FIXME: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    render={({ input }: any) => (
                      <Checkbox
                        {...input}
                        checked={input.value}
                        description="All team members will have access to this view"
                        id="is-shared"
                      >
                        Save as shared view
                      </Checkbox>
                    )}
                  />
                </Block>
              )}
              <Heading size="xs" css={{ marginBottom: "9px" }} as="h3">
                Based on the following filters:
              </Heading>
              <CustomViewFilters
                fontSize="15px"
                lineHeight="28px"
                noMargin
                filtersForDisplay={activeFiltersForDisplay}
              />
            </Block>
          </Modal>
        </form>
      )}
    </Form>
  );
};

export default AddCustomViewModal;
